
import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';

const calculateDiscountPercentage = (original, discounted) => {
  if (!original || !discounted || original <= discounted) return 0;
  return Math.round(((original - discounted) / original) * 100);
};

const getEffectivePrice = (product) => {
  if (product.originalPrice && product.discountedPrice && 
      product.originalPrice > product.discountedPrice) {
    return product.discountedPrice;
  }
  return product.price;
};

const ProductCard = memo(({ 
  product, 
  index,
  cartItems,
  handleAddToCart,
  handleRemoveFromCart,
  handleViewDetails,
  handleProceedToCart
}) => {
  const cartItem = cartItems.find(item => item.id === product.id);
  const quantity = cartItem ? cartItem.quantity : 0;
  const effectivePrice = getEffectivePrice(product);
  const totalPrice = (effectivePrice * quantity).toFixed(2);
  const discountPercentage = calculateDiscountPercentage(product.originalPrice, product.discountedPrice);

  return (
    <motion.div
      className="card border-0 shadow-sm h-100 position-relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      layout
    >
      {/* Product Image & Badge */}
      <div
        className="position-relative"
        style={{ 
          height: '200px',
          cursor: 'pointer',
          overflow: 'hidden'
        }}
        onClick={() => handleViewDetails(product)}
      >
        {product.image ? (
          <motion.img
            src={product.image}
            alt={product.name}
            className="img-fluid w-100 h-100 object-fit-contain p-2"
            style={{ 
              objectPosition: 'center',
              maxHeight: '100%',
              maxWidth: '100%'
            }}
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.2 }}
          />
        ) : (
          <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
            <div className="text-center text-muted">
              <i className="bi bi-image display-4 mb-2"></i>
              <div className="fw-semibold">Coming Soon</div>
            </div>
          </div>
        )}

        {discountPercentage > 0 && (
          <div className="position-absolute top-0 start-0 m-2" style={{ zIndex: 2 }}>
            <span className="badge bg-danger fs-6 px-2 py-1">{discountPercentage}% OFF</span>
          </div>
        )}

        {/* View Details Button */}
        <div className="position-absolute top-0 end-0 z-index-1 m-2">
          <button
            className="btn btn-light btn-sm rounded-circle shadow-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(product);
            }}
            title="View Details"
          >
            <i className="bi bi-eye"></i>
          </button>
        </div>
      </div>

      {/* Product Info */}
      <div className="card-body d-flex flex-column p-3">
        <div
          className="flex-grow-1 mb-2"
          style={{ cursor: 'pointer' }}
          onClick={() => handleViewDetails(product)}
        >
          <h6 className="card-title mb-1 text-dark fw-semibold line-clamp-2" style={{ fontSize: '0.9rem' }}>
            {product.name}
          </h6>
          {product.description && (
            <p className="card-text text-muted small mb-2 line-clamp-2" style={{ fontSize: '0.8rem' }}>
              {product.description}
            </p>
          )}

          {/* Price */}
          <div className="mb-2">
            {product.originalPrice && product.discountedPrice &&
            product.originalPrice > product.discountedPrice ? (
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold text-dark fs-6">${product.discountedPrice.toFixed(2)}</span>
                <span className="text-muted text-decoration-line-through small">
                  ${product.originalPrice.toFixed(2)}
                </span>
              </div>
            ) : (
              <span className="fw-bold text-dark fs-6">
                ${product.price?.toFixed(2) || '0.00'}
              </span>
            )}
          </div>
        </div>

        {/* Cart Actions */}
        <div className="mt-auto">
          {quantity === 0 ? (
            <button
              className="btn btn-outline-dark w-100 fw-semibold py-2"
              onClick={() => handleAddToCart(product)}
              style={{ fontSize: '0.8rem' }}
            >
              <i className="bi bi-cart-plus me-2"></i>
              Add to Cart
            </button>
          ) : (
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                <button
                  className="btn btn-outline-danger btn-sm py-1 px-2"
                  onClick={() => handleRemoveFromCart(product.id)}
                >
                  <i className="bi bi-dash"></i>
                </button>

                <span className="fw-bold text-logo">{quantity}</span>

                <button
                  className="btn btn-outline-success btn-sm py-1 px-2"
                  onClick={() => handleAddToCart(product)}
                >
                  <i className="bi bi-plus"></i>
                </button>
              </div>

              <div className="text-center small">
                <span className="text-muted">Total: </span>
                <span className="fw-bold text-black">${totalPrice}</span>
              </div>

              <button
                className="btn logo w-100 fw-semibold py-2"
                onClick={handleProceedToCart}
                style={{ fontSize: '0.8rem' }}
              >
                <i className="bi bi-bag-check me-2"></i>
                Proceed to Cart
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
});

const ProductGrid = ({ products, title = "Our Products" }) => {
  const { cartItems, addToCart, removeFromCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(12);
  const navigate = useNavigate();
  const observer = useRef();

  useEffect(() => {
    if (products && products.length > 0) {
      const timer = setTimeout(() => setLoading(false), 500);
      return () => clearTimeout(timer);
    }
  }, [products]);

  const handleAddToCart = useCallback((product) => {
    addToCart(product);
  }, [addToCart]);

  const handleRemoveFromCart = useCallback((productId) => {
    removeFromCart(productId);
  }, [removeFromCart]);

  const handleViewDetails = useCallback((product) => {
    navigate(`/product/${product.id}`, { state: { product } });
  }, [navigate]);

  const handleProceedToCart = useCallback(() => {
    navigate('/cart');
  }, [navigate]);

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && visibleCount < products.length) {
          setTimeout(() => {
            setVisibleCount(prev => Math.min(prev + 12, products.length));
          }, 300);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, visibleCount, products?.length]
  );

  const LoadingSkeleton = () => (
    <div className="container pt-4">
      <div className="row gx-3 gy-4">
        {[...Array(12)].map((_, index) => (
          <div key={index} className="col-6 col-sm-6 col-md-4 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="placeholder-glow">
                <div className="placeholder bg-light rounded-top" style={{ height: '200px' }}></div>
                <div className="card-body p-3">
                  <div className="placeholder col-8 mb-2"></div>
                  <div className="placeholder col-6 mb-2"></div>
                  <div className="placeholder col-7"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6 text-center">
          <div className="mb-4">
            <i className="bi bi-box-seam display-1 text-muted"></i>
          </div>
          <h3 className="text-muted mb-3">No Products Available</h3>
          <p className="text-muted">We're working hard to bring you amazing products. Check back soon!</p>
        </div>
      </div>
    </div>
  );

  const renderProductCard = useCallback((product, index) => (
    <div
      key={product.id}
      className="col-6 col-sm-6 col-md-4 col-lg-3 mb-3"
      ref={index === visibleCount - 1 ? lastProductRef : null}
    >
      <ProductCard
        product={product}
        index={index}
        cartItems={cartItems}
        handleAddToCart={handleAddToCart}
        handleRemoveFromCart={handleRemoveFromCart}
        handleViewDetails={handleViewDetails}
        handleProceedToCart={handleProceedToCart}
      />
    </div>
  ), [cartItems, handleAddToCart, handleRemoveFromCart, handleViewDetails, handleProceedToCart, lastProductRef, visibleCount]);

  if (loading) return <LoadingSkeleton />;
  if (!products || products.length === 0) return <EmptyState />;

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-bold text-dark mb-1">{title}</h2>
              <p className="text-muted mb-0">
                Showing {Math.min(visibleCount, products.length)} of {products.length} products
              </p>
            </div>
            {visibleCount < products.length && (
              <button
                className="btn btn-outline-primary"
                onClick={() => setVisibleCount(products.length)}
              >
                View All ({products.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className="row g-3">
        {products.slice(0, visibleCount).map((product, index) => 
          renderProductCard(product, index)
        )}
      </div>

      {/* Loading More */}
      {visibleCount < products.length && (
        <div className="text-center mt-5">
          <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="text-muted">Loading more products...</span>
        </div>
      )}

      {/* Back to Top */}
      {visibleCount > 12 && (
        <motion.button
          className="btn btn-primary position-fixed bottom-0 end-0 m-4 rounded-circle shadow-lg"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ zIndex: 1000, width: '50px', height: '50px' }}
        >
          <i className="bi bi-arrow-up"></i>
        </motion.button>
      )}
    </div>
  );
};

export default ProductGrid;